/**
 * The board module's data hooks (spec M1-B1 §6).
 *
 * Everything the board chrome needs, in one place, so no screen builds a tRPC call itself:
 * `useBoard`, `useBoardViews`, `useBoardMembers`, `useItemTerm` and the mutation set behind
 * the header menu, the view tabs and the create dialog. Every mutation invalidates the two
 * queries that can be stale afterwards — the board summary and the sidebar tree — rather than
 * refetching everything (F5's realtime patch replaces this in round 4).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { useBoardSummary } from '../shell/bootstrap';

export type BoardKind = 'public' | 'private' | 'share';

/** monday's UI words for the three `BoardKind` values `[B1§7.5]`; never the stored value. */
export const BOARD_KIND_LABEL: Record<BoardKind, string> = {
  public: 'Main',
  share: 'Shareable',
  private: 'Private',
};

export const BOARD_KIND_HINT: Record<BoardKind, string> = {
  public: 'Everyone at the account can see and edit it.',
  share: 'Everyone at the account can see it; guests can be invited to it.',
  private: 'Only the people you invite can see it.',
};

/** The item nouns the terminology dialog offers; free text is still accepted `[fl]`. */
export const ITEM_TERMINOLOGY_PRESETS = [
  'Item',
  'Task',
  'Project',
  'Contact',
  'Deal',
  'Lead',
  'Bug',
  'Epic',
  'Sprint',
  'Feedback',
  'Product',
  'Activity',
  'Account',
  'Employee',
] as const;

export function useBoard(boardId: string | null) {
  return useBoardSummary(boardId);
}

export function useBoardViews(boardId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.views.list.queryOptions({ boardId: boardId ?? '0' }),
    enabled: boardId !== null,
    staleTime: 30_000,
  });
}

export function useBoardMembers(boardId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.boards.members.queryOptions({ id: boardId ?? '0' }),
    enabled: boardId !== null,
    staleTime: 60_000,
  });
}

/**
 * The board **row** (`boards.get`), not the shell summary: the info popover needs
 * `created_by` and `created_at`, which `shell.board` does not carry. Fetched only while the
 * popover is open, so a board screen still paints on one `shell.bootstrap` round trip.
 */
export function useBoardRow(boardId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.boards.get.queryOptions({ id: boardId ?? '0' }),
    enabled: boardId !== null,
    staleTime: 60_000,
  });
}

export function useBoardSubscribers(boardId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.boards.subscribers.list.queryOptions({ id: boardId ?? '0' }),
    enabled: boardId !== null,
    staleTime: 60_000,
  });
}

/**
 * §6: **every** user-facing string that would say "item" goes through this — 20 distinct
 * terminologies exist in the account and "Item" is only the default `[fl §Surprises]`.
 * An empty stored value is `NULL` and falls back (E11); a trailing space is preserved (B5).
 */
export function useItemTerm(boardId: string | null): { singular: string; plural: string } {
  const board = useBoardSummary(boardId);
  return itemTermOf(board.data?.itemTerminology ?? null);
}

export function itemTermOf(
  singular: string | null,
  plural?: string | null,
): { singular: string; plural: string } {
  const one = singular && singular.length > 0 ? singular : 'Item';
  return { singular: one, plural: plural && plural.length > 0 ? plural : pluralise(one) };
}

/** monday stores the plural, but most boards leave it unset; this is the fallback only. */
export function pluralise(noun: string): string {
  const trimmed = noun.trimEnd();
  const suffix = noun.slice(trimmed.length);
  if (/(s|x|z|ch|sh)$/i.test(trimmed)) return `${trimmed}es${suffix}`;
  if (/[^aeiou]y$/i.test(trimmed)) return `${trimmed.slice(0, -1)}ies${suffix}`;
  return `${trimmed}s${suffix}`;
}

/**
 * Invalidates every read after a board / workspace / folder mutation.
 *
 * A narrower predicate was tried first and got it wrong: creating a workspace also changes
 * the *switcher's* list (`shell.workspaces`), creating a folder changes the tree, and a
 * favourite changes two unrelated screens. These mutations happen at human speed, so
 * refetching what is on screen costs nothing and cannot be subtly stale. Round 4's realtime
 * patch (F5) replaces this with a targeted update.
 */
export function useBoardInvalidation() {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries();
  }, [queryClient]);
}

export interface CreateBoardInput {
  name: string;
  kind: BoardKind;
  workspaceId?: string;
  folderId?: string;
  description?: string;
  itemTerminology?: string;
  itemTerminologyPlural?: string;
  templateId?: string;
  empty?: boolean;
}

export function useBoardMutations() {
  const client = useTRPCClient();
  const invalidate = useBoardInvalidation();

  const create = useMutation({
    mutationFn: (input: CreateBoardInput) => client.boards.create.mutate(input),
    onSuccess: invalidate,
  });

  /** `update_board` changes exactly one attribute per call `[sdl:BoardAttributes]`. */
  const update = useMutation({
    mutationFn: (input: {
      id: string;
      attribute: 'name' | 'description' | 'descriptionHtml' | 'itemTerminology' | 'communication';
      value: string | null;
      plural?: string | null;
    }) => client.boards.update.mutate(input),
    onSuccess: invalidate,
  });

  const changeKind = useMutation({
    mutationFn: (input: { id: string; kind: BoardKind }) => client.boards.changeKind.mutate(input),
    onSuccess: invalidate,
  });

  const duplicate = useMutation({
    mutationFn: (input: {
      id: string;
      duplicateType:
        | 'duplicate_board_with_structure'
        | 'duplicate_board_with_pulses'
        | 'duplicate_board_with_pulses_and_updates';
      name?: string;
      workspaceId?: string;
      folderId?: string;
      keepSubscribers?: boolean;
    }) => client.boards.duplicate.mutate(input),
    onSuccess: invalidate,
  });

  const archive = useMutation({
    mutationFn: (id: string) => client.boards.archive.mutate({ id }),
    onSuccess: invalidate,
  });

  const restore = useMutation({
    mutationFn: (id: string) => client.boards.restore.mutate({ id }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.boards.remove.mutate({ id }),
    onSuccess: invalidate,
  });

  const restoreFromTrash = useMutation({
    mutationFn: (id: string) => client.boards.restoreFromTrash.mutate({ id }),
    onSuccess: invalidate,
  });

  const move = useMutation({
    mutationFn: (input: { id: string; workspaceId?: string; folderId?: string | null }) =>
      client.boards.move.mutate(input),
    onSuccess: invalidate,
  });

  const setSubscribers = useMutation({
    mutationFn: (input: {
      mode: 'add' | 'remove' | 'setRole';
      id: string;
      userIds?: string[];
      teamIds?: string[];
      role?: 'owner' | 'subscriber';
    }) => {
      const { mode, ...rest } = input;
      if (mode === 'add') return client.boards.subscribers.add.mutate(rest);
      if (mode === 'remove') return client.boards.subscribers.remove.mutate(rest);
      return client.boards.subscribers.setRole.mutate(rest);
    },
    onSuccess: invalidate,
  });

  return {
    create,
    update,
    changeKind,
    duplicate,
    archive,
    restore,
    remove,
    restoreFromTrash,
    move,
    setSubscribers,
  };
}

export function useViewMutations(boardId: string) {
  const client = useTRPCClient();
  const invalidate = useBoardInvalidation();

  const create = useMutation({
    mutationFn: (input: { registryId: string; name?: string }) =>
      client.views.create.mutate({ boardId, ...input }),
    onSuccess: invalidate,
  });
  const rename = useMutation({
    mutationFn: (input: { viewId: string; name: string }) =>
      client.views.update.mutate({ boardId, viewId: input.viewId, name: input.name }),
    onSuccess: invalidate,
  });
  const duplicate = useMutation({
    mutationFn: (viewId: string) => client.views.duplicate.mutate({ boardId, viewId }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (viewId: string) => client.views.remove.mutate({ boardId, viewId }),
    onSuccess: invalidate,
  });
  /**
   * **P-151.** The inverse of `remove`. `views.remove` is soft (`services/views.ts` §5.5 keeps
   * the row so `restore_view` can bring it back), `viewsRouter.restore` has been there since
   * B1 — and nothing in the web app called it, so the delete toast had no Undo to offer.
   */
  const restore = useMutation({
    mutationFn: (viewId: string) => client.views.restore.mutate({ boardId, viewId }),
    onSuccess: invalidate,
  });
  const setDefault = useMutation({
    mutationFn: (viewId: string) => client.views.setDefault.mutate({ boardId, viewId }),
    onSuccess: invalidate,
  });
  const setVisibility = useMutation({
    mutationFn: (input: { viewId: string; visibility: 'shared' | 'personal' }) =>
      client.views.update.mutate({ boardId, viewId: input.viewId, visibility: input.visibility }),
    onSuccess: invalidate,
  });
  const reorder = useMutation({
    mutationFn: (input: { viewId: string; placement: 'top' | 'bottom' | { method: 'before_at' | 'after_at'; relativeTo: string } }) =>
      client.views.reorder.mutate({ boardId, viewId: input.viewId, placement: input.placement }),
    onSuccess: invalidate,
  });
  /** "Remember the last view" is per user, per board (§7.6, F4 §4.2). */
  const rememberLastViewed = useMutation({
    mutationFn: (viewId: string | null) => client.views.rememberLastViewed.mutate({ boardId, viewId }),
  });

  return { create, rename, duplicate, remove, restore, setDefault, setVisibility, reorder, rememberLastViewed };
}

/* ─────────────────────────────── the board people dialog's typeahead (P-043) ── */

/**
 * The account directory, filtered server-side, for the *Invite to this board* typeahead.
 *
 * `account.users.list` needs `account.users.read`, which a **guest** does not have (F8 §5.3).
 * That is not an error state here: a guest may not add anybody to a board either, so the query
 * is simply disabled for them and the dropdown offers the invite row alone (which they also do
 * not get). `retry: false` keeps a denial from being retried three times per keystroke.
 */
export function useAccountPeopleSearch(query: string, enabled: boolean) {
  const trpc = useTRPC();
  const term = query.trim();
  return useQuery({
    ...trpc.account.users.list.queryOptions({ search: term, limit: 50 }),
    enabled: enabled && term.length > 0,
    retry: false,
    staleTime: 30_000,
  });
}

/** The same typeahead's team rows. monday's picker searches *"name, team, or email address"*. */
export function useTeamSearch(query: string, enabled: boolean) {
  const trpc = useTRPC();
  const term = query.trim();
  return useQuery({
    ...trpc.admin.teams.list.queryOptions({ search: term }),
    enabled: enabled && term.length > 0,
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * The two writes the typeahead makes, beyond `setSubscribers`.
 *
 * `inviteToBoard` is one call: `admin.invitations.invite` carries `boardId`, and the service
 * subscribes every address it created before it answers (T-9q) — so the client never has to
 * chain an invite into an add and cannot leave a half-done state behind if the second call
 * fails. `addTeam` expands a team to its members, because `boards.members` — the list this
 * dialog renders — is a list of people, and a team-shaped subscription would be invisible in it.
 */
export function useSubscriberPickerMutations(boardId: string) {
  const client = useTRPCClient();
  const invalidate = useBoardInvalidation();

  const inviteToBoard = useMutation({
    mutationFn: (input: { email: string; role: 'member' | 'guest' }) =>
      client.admin.invitations.invite.mutate({
        emails: [input.email],
        role: input.role,
        boardId,
      }),
    onSuccess: invalidate,
  });

  const addTeam = useMutation({
    mutationFn: async (teamId: string) => {
      const team = await client.admin.teams.get.query({ teamId });
      const userIds = team.members.map((member) => member.userId);
      if (userIds.length === 0) return { added: 0 };
      await client.boards.subscribers.add.mutate({ id: boardId, userIds, role: 'subscriber' });
      return { added: userIds.length };
    },
    onSuccess: invalidate,
  });

  return { inviteToBoard, addTeam };
}
